import { useCanOpenRoom } from "../../domains/room/hooks/useCanOpenRoom";
import { cjsDefault } from "../../lib/cjs-default";
import { RoomJoinOffer } from "../../components/room/DiscoverableRoomBlock";
import { useSharedContentBack } from "../../domains/room/hooks/useSharedContentBack";
import reactFadeIn from "react-fade-in/lib/FadeIn";
import { displayToast } from "../../components/app/AppToast";

import ContentHeader from "../../components/app/headers/ContentHeader";
import Loader from "../../components/Loader";
import SharedBySheet from "../../components/SharedBySheet";
import VideoStoryScreen from "../../components/videos/VideoStoryScreen";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { handleErrorContentNotFound } from "../../domains/app/utils/errorHandler";
import { isUserAuthentified } from "../../domains/user/utils/isUserAuthentified";
import { useMeasuredHeight } from "../../hooks/use-measured-height";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventPageView } from "../../tools/analytics/common.analytics";
import { gaEventViewItemVideo } from "../../tools/analytics/domains/video.analytics";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import VideoStoryDrawer from "./VideoStoryDrawer";
import { useTranslation } from "react-i18next";
import {
  apiClient,
  getApiQueryData,
  useApiQuery,
  type Api,
} from "../../services/api";
import { useCongressAttributionFromNavigation } from "../../domains/congress/congressAttribution";

const FadeIn = cjsDefault(reactFadeIn);

/** One video story, at the full height under the header. */
export default function VideoStoryPage({
  slug,
  roomId,
}: {
  slug: string;
  roomId: string | null;
}) {
  // The row only dresses the page (logo, analytics). A shared link brings
  // readers the row refuses (no session, or not in a private room), and the
  // refusal would only reach Sentry: ask only when the reader can open it.
  const canOpenRoom = useCanOpenRoom(roomId);
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null && canOpenRoom === "open"
      ? { param: { id: roomId } }
      : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  // Stamped by the card that navigated here; null on every other entry.
  const congressAttribution = useCongressAttributionFromNavigation();
  const { t } = useTranslation();
  const handleBack = useSharedContentBack(roomId);
  const header = useMeasuredHeight();
  const storyInput = { param: { slug } };
  const storyQuery = useApiQuery(
    "/webapp/public/content/slug/:slug",
    storyInput
  );
  // `state: "error"` does not mean "never loaded": `createQueryResult` checks
  // the error before the data, so a refetch that fails on focus or reconnect
  // reports an error while the cache still holds the story. Keep showing it,
  // or the player unmounts under the reader.
  const cachedStory =
    storyQuery.state === "error"
      ? getApiQueryData("/webapp/public/content/slug/:slug", storyInput)
      : null;
  const storyBody =
    storyQuery.state === "success" ? storyQuery.body : cachedStory;
  const story = storyBody === null ? null : videoStory(storyBody);

  // A missing story leaves for Discovery with a toast. Any other failure keeps
  // the story already shown, or, on a first load, the page with its back
  // button and a toast.
  useEffectOnce(() => {
    if (storyQuery.state !== "error") return;
    if (handleErrorContentNotFound(storyQuery.error)) return;
    if (cachedStory !== null) return;
    displayToast(t("Something went wrong. Please try again."), "error");
  }, [storyQuery.state]);

  useEffectOnce(() => {
    if (story === null) return;
    void (async () => {
      try {
        await gaEventPageView(currentRoom);
        if (await isUserAuthentified()) {
          await apiClient.post("/webapp/private/content/:id/views", {
            param: { id: story.id },
          });
        }
        gaEventViewItemVideo(
          {
            video: story,
            listName: AnalyticsItemList.VIDEO_ARTICLE_PAGE,
          },
          congressAttribution
        );
      } catch {
        // api error already captured by onRequestError.
      }
    })();
  }, [story === null ? null : story.id]);

  if (storyQuery.state === "pending")
    return <Loader expanded style={{ background: "#000" }} />;

  return (
    <ScrollableLayout
      background="linear-gradient(180deg, rgba(49, 59, 66, 0.4) 0%, rgba(210, 220, 226, 0) 100%)"
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <div ref={header.ref}>
          {story === null ? (
            <ContentHeader transparent onBack={handleBack} />
          ) : (
            <ContentHeader
              transparent
              title={story.title}
              onBack={handleBack}
              actions={<VideoStoryDrawer story={story} />}
            />
          )}
          {/* In the header, so the story below keeps its measured height. */}
          {roomId !== null && <RoomJoinOffer roomId={roomId} />}
        </div>
      }
    >
      {story !== null && (
        <FadeIn delay={1500}>
          {/* Clips the player's controls to the story: on a short screen the
              action column would rise over the header and take its taps. */}
          <div
            className="relative isolate overflow-hidden"
            style={{
              height: `calc(100dvh - ${header.height}px - var(--safe-area-top))`,
            }}
          >
            {/* One player per story: a back navigation between two stories
                must not hand one story's like state to the other. */}
            <VideoStoryScreen
              key={story.id}
              height="100%"
              video={story}
              analyticsListName={AnalyticsItemList.VIDEO_STORY_FEED}
              roomId={roomId}
            />
          </div>
        </FadeIn>
      )}

      <SharedBySheet />
    </ScrollableLayout>
  );
}

type ContentDetail =
  Api["get"]["/webapp/public/content/slug/:slug"]["output"]["body"];

// Every story link names a Video: another format here is a broken link.
function videoStory(content: ContentDetail) {
  switch (content.content_format) {
    case "Video":
      return content;
    case "Audio":
    case "Infographic":
    case "Nectar":
      throw new Error(
        `Expected Video from /webapp/public/content/slug/:slug, got ${content.content_format}`
      );
    default:
      throw new Error(content satisfies never);
  }
}
